/** Criar a senha nova pelo link recebido por e-mail.
 *
 * Pública por definição — quem chega aqui não consegue entrar. O link traz a
 * loja e o token; antes do formulário a página confere o link, para dizer
 * logo "este link expirou" em vez de deixar a pessoa digitar duas senhas à toa.
 */

import { useMemo, useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import { mensagemDeErro } from '../api/client';
import { ApiSenha } from '../api/senha';
import { Icone } from '../components/Icone';
import { NOME_DO_SISTEMA, TelaDeAcesso } from '../components/TelaDeAcesso';

/** Mesma política do servidor (`validate_password_strength`): só comprimento.
 *  As regras de composição saíram — ver o porquê lá. */
const REGRAS: { rotulo: string; ok: (s: string) => boolean }[] = [
  { rotulo: 'Pelo menos 8 caracteres', ok: (s) => s.length >= 8 },
  { rotulo: 'Não ser uma senha óbvia', ok: (s) => new Set(s.trim().toLowerCase()).size >= 3 },
];

export function RedefinirSenha() {
  const [params] = useSearchParams();
  const navegar = useNavigate();
  const loja = params.get('loja') ?? '';
  const token = params.get('token') ?? '';

  const [senha, setSenha] = useState('');
  const [confirmacao, setConfirmacao] = useState('');
  const [mostrar, setMostrar] = useState(false);
  const [erro, setErro] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [pronto, setPronto] = useState(false);

  const link = useQuery({
    queryKey: ['redefinicao', loja, token],
    queryFn: () => ApiSenha.conferir(loja, token),
    enabled: Boolean(loja && token),
    retry: false,
    staleTime: Infinity,
  });

  const cumpridas = useMemo(() => REGRAS.map((r) => r.ok(senha)), [senha]);
  const senhaBoa = cumpridas.every(Boolean);
  const confere = confirmacao.length > 0 && confirmacao === senha;

  const enviar = async (evento: FormEvent) => {
    evento.preventDefault();
    setErro('');
    if (!senhaBoa) return setErro('A senha ainda não cumpre todas as regras.');
    if (!confere) return setErro('As duas senhas não são iguais.');
    setEnviando(true);
    try {
      await ApiSenha.redefinir(loja, token, senha);
      setPronto(true);
    } catch (e) {
      setErro(mensagemDeErro(e, 'Não foi possível salvar a nova senha.'));
    } finally {
      setEnviando(false);
    }
  };

  const primeiro = link.data?.primeiro_acesso;
  const linkRuim = !loja || !token || link.isError;

  return (
    <TelaDeAcesso>
      <form className="login-box" onSubmit={enviar}>
        <div className="login-logo">
          <Icone nome={pronto ? 'check' : linkRuim ? 'alerta' : 'chave'} tamanho={28} />
        </div>

        {pronto ? (
          <>
            <div className="login-title">{primeiro ? 'Senha criada' : 'Senha alterada'}</div>
            <div className="login-sub">
              Pronto, Ir∴ {link.data?.nome}. Use seu CIM ou e-mail e a senha nova para entrar.
              {!primeiro && ' Por segurança, as sessões abertas em outros aparelhos foram encerradas.'}
            </div>
            <button
              type="button"
              className="login-btn"
              style={{ marginTop: 22 }}
              onClick={() => navegar('/login', { replace: true })}
            >
              ENTRAR
            </button>
          </>
        ) : linkRuim ? (
          <>
            <div className="login-title">Link indisponível</div>
            <div className="login-sub">
              {!loja || !token
                ? 'O endereço está incompleto. Abra o link direto do e-mail ou peça um novo.'
                : mensagemDeErro(link.error, 'Este link não é mais válido.')}
            </div>
            <Link
              to="/login?esqueci=1"
              className="login-btn"
              style={{
                marginTop: 22,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                textDecoration: 'none',
              }}
            >
              PEDIR UM NOVO LINK
            </Link>
          </>
        ) : link.isLoading ? (
          <>
            <div className="login-title">Conferindo o link…</div>
            <div className="login-sub">Só um instante.</div>
          </>
        ) : (
          <>
            <div className="login-title">{primeiro ? 'Crie sua senha' : 'Crie uma nova senha'}</div>
            <div className="login-sub">
              Olá, Ir∴ <strong>{link.data?.nome}</strong>.
              <br />
              {primeiro
                ? `Este é o seu primeiro acesso à ${NOME_DO_SISTEMA}.`
                : 'Escolha uma senha que você não use em outros sites.'}
            </div>

            <div className="login-separator">Nova senha</div>
            <div className="login-senha">
              <input
                className="login-input"
                type={mostrar ? 'text' : 'password'}
                placeholder="Nova senha"
                value={senha}
                onChange={(e) => setSenha(e.target.value)}
                autoComplete="new-password"
                autoFocus
                disabled={enviando}
              />
              <button
                type="button"
                className="login-olho"
                onClick={() => setMostrar((v) => !v)}
                aria-label={mostrar ? 'Esconder senha' : 'Mostrar senha'}
                title={mostrar ? 'Esconder senha' : 'Mostrar senha'}
              >
                <Icone nome={mostrar ? 'olhoFechado' : 'olho'} tamanho={17} />
              </button>
            </div>
            <input
              className="login-input"
              type={mostrar ? 'text' : 'password'}
              placeholder="Repita a nova senha"
              value={confirmacao}
              onChange={(e) => setConfirmacao(e.target.value)}
              autoComplete="new-password"
              disabled={enviando}
            />

            <ul className="login-regras" aria-label="Regras da senha">
              {REGRAS.map((r, i) => (
                <li key={r.rotulo} className={cumpridas[i] ? 'ok' : ''}>
                  <Icone nome={cumpridas[i] ? 'check' : 'fechar'} tamanho={13} espessura={2.4} />
                  {r.rotulo}
                </li>
              ))}
              <li className={confere ? 'ok' : ''}>
                <Icone nome={confere ? 'check' : 'fechar'} tamanho={13} espessura={2.4} />
                As duas senhas iguais
              </li>
            </ul>

            <button className="login-btn" type="submit" disabled={enviando || !senhaBoa || !confere}>
              {enviando ? 'SALVANDO…' : primeiro ? 'CRIAR SENHA' : 'SALVAR NOVA SENHA'}
            </button>
            <div className="login-err" role="alert">
              {erro}
            </div>
          </>
        )}

        <div className="login-links">
          <Link to="/login" className="login-link">
            ← Voltar para o login
          </Link>
        </div>
      </form>
    </TelaDeAcesso>
  );
}
